import React, { useEffect, useState, useMemo, useRef } from 'react'
import FilesView from './FilesView/FilesView'
import FileList from './FilesView/FileList'
import PreviewMapComponent from './Map/PreviewMapComponent'
import ErrorBoundary from './ErrorBoundary'
import { FaDownload, FaRegLightbulb } from 'react-icons/fa'
import { findFilesByKey, getTokenByAppName } from '../utils/common-utils'
import SideDrawer from './Download/SideDrawer'
import { ConditionalTooltip } from './FilesView/FilesView'
import BadgePill from './BadgePill'
import {
  trackEvent,
  DOWNLOAD_REPORT,
} from '../utils/googleAnalytics';

function Popup({ isOpen, onClose, details, spatialExtent }) {
  const [activeTab, setActiveTab] = useState('tree')
  const [infoTab, setInfoTab] = useState('general')
  const [archiveFiles, setArchiveFiles] = useState([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [fileName, setFileName] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [sizeInGB, setSizeInGB] = useState(0)
  const [instructionUrl, setInstructionUrl] = useState('')
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false)
  const popupRef = useRef(null);
  const [summaryModalStyle, setSummaryModalStyle] = useState({});
  const [modalOpenClass, setModalOpenClass] = useState(false);
  const [summaryFiles, setSummaryFiles] = useState([]);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const memoizedFilesView = useMemo(() => {
    if (details?.files) {
      return <FilesView files={details.files} />
    }
    return null
  }, [details?.files])

  useEffect(() => {
    if (isSummaryModalOpen) {
      // Wait for next tick to trigger transition
      setTimeout(() => setModalOpenClass(true), 10);
    } else {
      setModalOpenClass(false);
    }
  }, [isSummaryModalOpen]);

  const handleDownload = (archive) => {
    trackEvent(DOWNLOAD_REPORT, 'Download Report');
    const location = archive.path.split('|')
    const token = getTokenByAppName(location[0])
    const fileParts = archive.file.split('|')
    const fileName = fileParts[0]
    const size = parseInt(fileParts[1]) / 1024 ** 3
    const fileUrl =
      process.env.REACT_APP_TDIS_DATA_CATALOG_URL_BASE +
      '/' +
      encodeURIComponent(location[0]) +
      token
    const instructionUrl =
      process.env.REACT_APP_TDIS_DATA_CATALOG_URL_BASE +
      '/rbfs/InstructionsForStorageExplorerDownload.pdf' +
      token
    if (size > 10) {
      setFileName(fileName)
      setFileUrl(fileUrl)
      setSizeInGB(size)
      setInstructionUrl(instructionUrl)
      setIsDrawerOpen(true)
    } else {
      handleBrowserDownload(fileUrl, fileName)
    }
  }

  const handleBrowserDownload = (fileUrl, fileName) => {
    const anchor = document.createElement('a')
    anchor.target = '_parent'
    anchor.href = fileUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    setIsDrawerOpen(false)
  }

  useEffect(() => {
    if (isOpen && details) {
      // console.log(details.files)
      const archFiles = findFilesByKey(details.files, 'archive')
      console.log(archFiles)
      setArchiveFiles(archFiles)
      setSummaryFiles(archFiles[0])
    }
  }, [isOpen, details])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    } else {
      window.removeEventListener('keydown', handleKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const handleOpenSummaryModal = () => {
    if (popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      setSummaryModalStyle({
        position: 'fixed',
        top: rect.top + 'px',
        left: rect.left + 'px',
        width: rect.width + 'px',
        height: rect.height + 'px',
        zIndex: 9999,
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        overflowY: 'auto',
        padding: '2rem',
      });
    }
    setIsSummaryModalOpen(true);
  };

  if (!isOpen) return null

  if (!details) {
    return (
      <div className="popup-overlay">
        <div className="popup-content" ref={popupRef}>
          <div className="popup-header">
            <h2>Error</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/* <button
                aria-label="Show AI Summary"
                tabIndex={0}
                className="ai-summary-button"
                onClick={handleOpenSummaryModal}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpenSummaryModal();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px',
                  borderRadius: '50%',
                  fontSize: '1.5rem',
                  color: '#facc15',
                  transition: 'background 0.2s'
                }}
              >
                <FaRegLightbulb />
              </button> */}
              <button className="close-button" onClick={onClose}>
                X
              </button>
            </div>
          </div>
          <div className="popup-body">
            <p>Error loading data. Please try again later.</p>
          </div>
          <div className="popup-footer">
            <button className="close-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderField = (label, value) => (
    <>
      <h3>{label}:</h3>
      <p style={{ wordBreak: 'break-word' }}>
        {value ? (
          value
        ) : (
          <span className="black-text">
            N/A<span className="red-asterisk">*</span>
          </span>
        )}
      </p>
    </>
  )

  const renderArchiveFile = (archive) => {
    const fileParts = archive.file.split('|')
    const fileName = fileParts[0]
    const sizeInBytes = parseInt(fileParts[1])

    // Format size appropriately
    let sizeLabel
    if (sizeInBytes >= 1024 ** 3) { // GB
      sizeLabel = `${(sizeInBytes / 1024 ** 3).toFixed(2)} GB`
    } else if (sizeInBytes >= 1024 ** 2) { // MB
      sizeLabel = `${(sizeInBytes / 1024 ** 2).toFixed(1)} MB`
    } else { // KB
      sizeLabel = `${Math.round(sizeInBytes / 1024)} KB`
    }

    return (
      <div className="archive-file-container">
        <ConditionalTooltip content={fileName}>
          <span className="archive-file-name">{fileName}</span>
        </ConditionalTooltip>
        <div className="archive-file-actions">
          <span className="archive-file-size">
            <BadgePill
              label={sizeLabel}
              backgroundColor={sizeInBytes > (10 * 1024 ** 3) ? '#ff4d4f' : '#52c41a'}
              textColor="#ffffff"
            />
          </span>
          <FaDownload
            filename={archive.file}
            className="download-button archive-download-icon"
            onClick={() => {
              const location = archive.path.split('|');
              const token = getTokenByAppName(location[0]);
              const fileParts = archive.file.split('|');
              const fileName = fileParts[0];
              const fileUrl =
                process.env.REACT_APP_TDIS_DATA_CATALOG_URL_BASE +
                '/' +
                encodeURIComponent(location[0]) +
                token;
              setPendingDownload({ fileUrl, fileName });
              setShowDisclaimer(true);
            }}
          />
        </div>
      </div>
    )
  }

  const renderFieldForParameters = (label, value) => (
    <>
      <h3>{label}:</h3>
      <p>
        {value === 'None' ? (
          <span className="black-text">
            N/A<span className="red-asterisk">*</span>
          </span>
        ) : value ? (
          value
        ) : (
          <span className="black-text">
            N/A<span className="red-asterisk">*</span>
          </span>
        )}
      </p>
    </>
  )

  const StaticTooltip = ({ content, children }) => {
    return (
      <div className="static-tooltip-container">
        {children}
        <span className="static-tooltip-text">{content}</span>
      </div>
    );
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content" ref={popupRef}>
        <div className="popup-header">
          <h2>{details.title ? details.title : details.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="close-button" onClick={onClose}>
              X
            </button>
          </div>
        </div>
        <div className="popup-body">
          <div className="popup-section left-section">
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className="tabs">
                <div
                  className={`tab ${infoTab === 'general' ? 'active' : ''}`}
                  onClick={() => setInfoTab('general')}
                >
                  General Information
                </div>
                <div
                  className={`tab ${infoTab === 'contact' ? 'active' : ''}`}
                  onClick={() => setInfoTab('contact')}
                >
                  Contact Information
                </div>
              </div>
              <div className="tab-content">
                {infoTab === 'general' && (
                  <div>
                    {(details.simulation_description ||
                      details.simulation_temporal_extent ||
                      details.simulation_temporal_resolution) &&
                      renderField('Model Name', details.model_name)}

                    {details.simulation_description &&
                      renderField(
                        'Simulation Description',
                        details.simulation_description
                      )}
                    {details.simulation_temporal_extent &&
                      renderField(
                        'Simulation Temporal Extent',
                        details.simulation_temporal_extent
                      )}
                    {details.simulation_temporal_resolution &&
                      renderField(
                        'Simulation Temporal Resolution',
                        details.simulation_temporal_resolution
                      )}

                    {renderField('HUC', details.huc_code)}
                    {renderField('County', details.county_name)}
                    {renderField('City', details.city_name)}
                    {renderField('Model Description', details.description)}
                    {renderField('Model Purpose', details.model_purpose)}
                    {renderField('Update Frequency', details.update_frequency)}
                    {renderField('Model Type', details.model_type)}
                    {renderField('Model Software Name', details.model_software_name)}
                    {renderField(
                      'Model Software version',
                      details.model_software_version
                    )}
                    {renderField('Time Period', details.time_period)}
                    {renderField('Model Last Updated', details.model_last_updated_dt)}
                  </div>
                )}
                {infoTab === 'contact' && (
                  <div>
                    {renderField('Contact Affiliation', details.contact_affiliation)}
                    {renderField('Contact Department', details.contact_department)}
                    {renderField('Contact Name', details.contact_name)}
                    {renderField('Contact Email', details.contact_email)}
                    {renderField('Contact Phone number', details.contact_phone_number)}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="popup-section middle-section">
            {spatialExtent ? (
              <PreviewMapComponent data={spatialExtent} details={details} />
            ) : (
              <div className="loading-container">
                <p className="loading-style">Loading Spatial Extents...</p>
              </div>
            )}
          </div>
          <div className="popup-section right-section">
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Tabs */}
              <div className="tabs">
                <StaticTooltip content="Searchable listing of files within the model package.">
                  <div
                    className={`tab ${activeTab === 'tree' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tree')}
                  >
                    Model Files Listing
                  </div>
                </StaticTooltip>
                <StaticTooltip content="Download model package. Additional files are available for some of the models.">
                  <div
                    className={`tab ${activeTab === 'downloads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('downloads')}
                  >
                    Available Files for Download
                  </div>
                </StaticTooltip>
                {/* <StaticTooltip content="Download model package. Additional files are available for some of the models.">
                  <div
                    className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai')}
                  >
                    AI Summary
                  </div>
                </StaticTooltip> */}
              </div>


              {/* Tab Content */}
              <div className="tab-content" style={{ paddingBottom: 20 }}>
                {activeTab === 'tree' && (
                  <div>
                    <div>
                      <ErrorBoundary>
                        {details?.files ? (
                          memoizedFilesView
                        ) : (
                          <div className="loading-container">
                            <p className="loading-style">Loading...</p>
                          </div>
                        )}
                      </ErrorBoundary>

                      {details.input_files && details.input_files.length > 0 ? (
                        <div>
                          <br />
                          <h3>Input Files</h3>
                          <FileList files={details.input_files} />
                        </div>
                      ) : (
                        <div></div>
                      )}

                      {details.output_files &&
                        details.output_files.length > 0 ? (
                        <div>
                          <br />
                          <h3>Output Files</h3>
                          <FileList files={details.output_files} />
                        </div>
                      ) : (
                        <div></div>
                      )}

                      {details.parameters && details.parameters.length > 0 ? (
                        <div>
                          <br />
                          <h3>Parameters</h3>
                          <br></br>
                          {details.parameters.map((param) => {
                            const [key, value] = Object.entries(param)[0]
                            return renderFieldForParameters(key, value)
                          })}
                        </div>
                      ) : (
                        <div>
                          <br></br>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === 'downloads' && (
                  <>
                    <div>
                      {/* <h3>
                        <u>Complete Model Package</u>
                      </h3>
                      {archiveFiles.map((archive, index) => {
                        if (archive.file.toLowerCase().includes('final.zip')) {
                          return renderArchiveFile(archive, index);
                        }
                        return null;
                      })} */}
                      {details.source_application && details.source_application.toLowerCase() === 'rbfs' ? (
                        <>
                          <h3><u>Complete Model Package</u></h3>
                          {archiveFiles.length > 0 && renderArchiveFile(archiveFiles[0], 0)}
                        </>
                      ) : (
                        <>
                          <h3><u>Complete Model Package</u></h3>
                          {archiveFiles.map((archive, index) => {
                            if (!archive.file.toLowerCase().includes('results')) {
                              return renderArchiveFile(archive, index);
                            }
                            return null;
                          })}
                        </>
                      )}


                      {details.source_application &&
                        details.source_application.toLowerCase() === 'rbfs' && (
                          <>
                            <br />
                            <h3>
                              <u>Model Results</u>
                            </h3>
                            {archiveFiles.map((archive, index) => {
                              if (
                                archive.file.toLowerCase().includes('results')
                              ) {
                                return renderArchiveFile(archive, index)
                              }
                            })}

                          </>
                        )}

                      {archiveFiles.some(f => f.file.toLowerCase().includes('.pdf') || f.file.toLowerCase().includes('.pptx')) &&
                        details.source_application &&
                        details.source_application.toLowerCase() === 'rbfs' && (
                          <>
                            <br />
                            <h3>
                              <u>Reports</u>
                            </h3>
                            {archiveFiles.map((archive, index) => {
                              const fileName = archive.file.toLowerCase();
                              if (fileName.includes('.pdf') || fileName.includes('.pptx')) {
                                return renderArchiveFile(archive, index);
                              }
                            })}
                          </>
                        )}

                      {archiveFiles.some(file => {
                        const name = file.file.toLowerCase();
                        return (
                          name.includes('.zip') &&
                          !name.includes('final.zip') &&
                          !name.includes('results')
                        );
                      }) && details.source_application &&
                        details.source_application.toLowerCase() === 'rbfs' && (
                          <>
                            <br />
                            <h3><u>Spatial Data</u></h3>
                            {archiveFiles.slice(1).map((file, index) => {
                              const name = file.file.toLowerCase();
                              if (
                                name.includes('.zip') &&
                                !name.includes('final.zip') &&
                                !name.includes('results')
                              ) {
                                return renderArchiveFile(file, index);
                              }
                              return null;
                            })}
                          </>
                        )}

                      <SideDrawer
                        isOpen={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        fileName={fileName}
                        sizeInGB={sizeInGB}
                        fileUrl={fileUrl}
                        instructionUrl={instructionUrl}
                        handleBrowserDownload={() =>
                          handleBrowserDownload(fileUrl, fileName)
                        }
                      />
                    </div>
                    <br />
                    <br />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="popup-footer">
          <p>
            N/A<span className="red-asterisk">*</span> - Not Available/Not
            Applicable/Not Specified
          </p>
          <button className="close-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {showDisclaimer && (
        <div className="popup-overlay">
          <div className="popup-content" style={{ maxWidth: 600 }}>
            <div className="popup-header">
              <h2 style={{ fontSize: '1.1rem' }}>Disclaimer</h2>
            </div>
            <div className="popup-body" style={{ maxHeight: 400, overflowY: 'auto', fontSize: '0.92rem', lineHeight: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
              <p style={{ fontSize: '0.92rem', width: '100%', textAlign: 'left' }}>
                The enclosed hydrologic and hydraulic models, final report, and GIS data were developed for the Texas General Land Office (GLO) for its Regional River Basin Flood Study project. The purpose of these models, final report, and associated GIS data is to better understand existing conditions for the GLO study. These products should not be used for construction or bidding purposes. For areas where FEMA effective FIRM maps do not include regulatory flood zones, or where Zone A is depicted, these maps could be considered best available data at the discretion of the local floodplain administrator. Enclosed models, reports, and GIS data may require additional effort to support a FEMA map update. The Texas General Land Office and any other relevant parties make no representations or warranties regarding the accuracy or completeness of the information provided in the enclosed documents or the data from which they were produced. No representations or warranties are made regarding the utility of any of the data or data representations for purposes other than those stated above. Not for use beyond those included in and specific to the scope of the RBFS project.
              </p>
              <div style={{ margin: '0.5rem 0 0.1rem 0', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="disclaimer-accept"
                    checked={disclaimerAccepted}
                    onChange={e => setDisclaimerAccepted(e.target.checked)}
                    style={{ marginRight: '0.5rem', width: 16, height: 16 }}
                  />
                  <label htmlFor="disclaimer-accept" style={{ userSelect: 'none', cursor: 'pointer' }}>
                    I have read and accept the terms and conditions
                  </label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', width: '100%' }}>
                  <button
                    className="close-button"
                    style={{ background: '#87bcbf', color: '#fff', fontSize: '0.95rem', minWidth: 90, opacity: disclaimerAccepted ? 1 : 0.6, cursor: disclaimerAccepted ? 'pointer' : 'not-allowed' }}
                    disabled={!disclaimerAccepted}
                    onClick={() => {
                      setShowDisclaimer(false);
                      setDisclaimerAccepted(false);
                      if (pendingDownload) {
                        handleBrowserDownload(pendingDownload.fileUrl, pendingDownload.fileName);
                        setPendingDownload(null);
                      }
                    }}
                  >
                    Agree
                  </button>
                  <button
                    className="close-button"
                    style={{ background: '#ccc', color: '#333', fontSize: '0.95rem', minWidth: 90 }}
                    onClick={() => {
                      setShowDisclaimer(false);
                      setDisclaimerAccepted(false);
                      setPendingDownload(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Popup
